import { useEffect, useState } from 'react';
import { Pagination } from '@quero-delivery/quero-components-web';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash/isEmpty';
import { useNavigate } from 'react-router';

import { useSearchParams } from 'react-router-dom';
import { Form } from '@radix-ui/react-form';
import { Button } from '@querodelivery/uni-react';
import styles from './FranchiseList.module.scss';

import FranchiseCard from '@/Components/fare/franchise-card/FranchiseCard';
import yup from '@/infra/YupDecorator';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { useGetFares } from '@/api/fares/get-fares';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import {
  DropdownInput,
  type IDropdownInputOptions,
} from '@/_components/dropdown-input/dropdown-input';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';
import { useCityFareFormAccess } from '@/features/fare/hooks/use-city-fare-form-access';
import { useLinkedMunicipioIds } from '@/features/fare/hooks/use-linked-municipio-ids';

const FILTER_DROPDOWN_OPTIONS: IDropdownInputOptions[] = [
  { value: 'true', text: 'Ativo', key: 'true' },
  { value: 'false', text: 'Inativo', key: 'false' },
];

const FranchiseFormSchema = yup.object({
  cityId: yup.string().optional(),
  isCloud: yup.string().optional(),
  hasCloudOperationOnCity: yup.string().optional(),
});

type TypeFranchiseForm = yup.InferType<typeof FranchiseFormSchema>;

const limit = DEFAULT_LIMIT_REQUEST;

function FranchiseList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { isLogisticOperator } = useCityFareFormAccess();
  const {
    scopedCityOptions,
    isMunicipioAllowed,
    isCheckingLinkedMunicipios,
    loadingCities,
  } = useLinkedMunicipioIds();

  const [page, setPage] = useState(1);

  const canCreateCityFare = !isLogisticOperator;

  const cityIdParams = searchParams.get('cityId');
  const isCloudParams = searchParams.get('isCloud');
  const hasCloudOperationOnCityParams = searchParams.get(
    'hasCloudOperationOnCity',
  );

  const allowedCityIdParams =
    cityIdParams && isMunicipioAllowed(cityIdParams) ? cityIdParams : '';

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TypeFranchiseForm>({
    defaultValues: {
      cityId: allowedCityIdParams,
      isCloud: isCloudParams || '',
      hasCloudOperationOnCity: hasCloudOperationOnCityParams || '',
    },
    resolver: yupResolver(FranchiseFormSchema),
  });

  const watchCityId = watch('cityId');
  const watchIsCloud = watch('isCloud');
  const watchHasCloudOperationOnCity = watch('hasCloudOperationOnCity');

  useEffect(() => {
    if (isCheckingLinkedMunicipios) {
      return;
    }

    if (cityIdParams && !isMunicipioAllowed(cityIdParams)) {
      setValue('cityId', '');
      setSearchParams(
        currentParams => {
          const nextParams = new URLSearchParams(currentParams);
          nextParams.delete('cityId');
          return nextParams;
        },
        { replace: true },
      );
    }
  }, [
    cityIdParams,
    isCheckingLinkedMunicipios,
    isMunicipioAllowed,
    setSearchParams,
    setValue,
  ]);

  const { data: fares, isLoading: loadingFares } = useGetFares({
    cityId: watchCityId || '',
    ...(watchIsCloud === 'true' && { isCloud: true }),
    ...(watchIsCloud === 'false' && { isCloud: false }),
    ...(watchHasCloudOperationOnCity === 'true' && {
      hasCloudOperationOnCity: true,
    }),
    ...(watchHasCloudOperationOnCity === 'false' && {
      hasCloudOperationOnCity: false,
    }),
    limit,
    offset: (page - 1) * limit,
  });

  const faresQuantity = fares?.totalCount || 0;
  const faresData = fares?.data || [];

  const handleSubmitForm: SubmitHandler<TypeFranchiseForm> = data => {
    const selectedCityId =
      data?.cityId && isMunicipioAllowed(data.cityId) ? data.cityId : '';

    setPage(1);
    setSearchParams({
      cityId: selectedCityId,
      ...(data?.isCloud === 'true' || data?.isCloud === 'false'
        ? { isCloud: data.isCloud }
        : {}),
      ...(data?.hasCloudOperationOnCity === 'true' ||
      data?.hasCloudOperationOnCity === 'false'
        ? { hasCloudOperationOnCity: data.hasCloudOperationOnCity }
        : {}),
    });
  };

  return (
    <div className="container">
      <div className={styles.franchiseHeader}>
        <Form
          className={styles.search}
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          <Controller
            name="cityId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Município"
                options={scopedCityOptions}
                loading={loadingCities || isCheckingLinkedMunicipios}
                value={value}
                search
                placeholder="Município"
                errorMessage={errors.cityId?.message}
                onChange={onChange}
              />
            )}
          />

          <Controller
            name="isCloud"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Cadastro de entregadores"
                options={FILTER_DROPDOWN_OPTIONS}
                value={value}
                placeholder="Cadastro de entregadores"
                errorMessage={errors.isCloud?.message}
                onChange={onChange}
              />
            )}
          />

          <Controller
            name="hasCloudOperationOnCity"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Cadastro de parceiros"
                options={FILTER_DROPDOWN_OPTIONS}
                value={value}
                placeholder="Cadastro de parceiros"
                errorMessage={errors.hasCloudOperationOnCity?.message}
                onChange={onChange}
              />
            )}
          />

          {canCreateCityFare && (
            <Button
              className={styles.createButton}
              size="lg"
              onClick={() => navigate('/tarifas/municipio/criar')}
            >
              <span className="font-medium">Criar tarifa</span>
            </Button>
          )}
        </Form>
      </div>

      <div>
        {loadingFares ? (
          <div className={styles.skeletonContainer}>
            <Skeleton
              height={17}
              width={100}
              borderRadius={8}
              className={styles.totalCount}
            />
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={91} borderRadius={20} />
            ))}
          </div>
        ) : (
          <>
            <p className="text-right mgn-bottom-sm">{faresQuantity} tarifas</p>

            <div className={styles.franchiseList}>
              {isEmpty(faresData) ? (
                <TableEmptyState
                  title="Nenhuma tarifa encontrada"
                  description="Não encontramos tarifas com os filtros selecionados."
                />
              ) : (
                <>
                  {faresData.map(fare => (
                    <FranchiseCard key={fare._id} franchise={fare} />
                  ))}
                  <Pagination
                    limitPerPage={limit}
                    currentPage={page}
                    totalCount={faresQuantity}
                    onPageChange={page => {
                      setPage(page);
                    }}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FranchiseList;
